import { DateTime } from '@ksyos/date-time';
import {
    Box,
    Button,
    Card,
    DatePicker,
    FormGroup,
    Link,
    PageHeader,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    TextInput,
} from '@ksyos/design-system';
import { useNavigate, useSearchParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { ValidationErrors } from '@frontend/utils';
import { SortDirection } from '@common/enums';
import { JobStatus } from '@common/enums/jobStatus';
import { ManagerAuthorization } from '@common/enums/managerAuthorization';
import { tryParseInt } from '@common/utils/tryParseInt';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { NullableFormattedTime } from '@frontend/components/elements/NullableFormattedTime';
import { EnumSelect } from '@frontend/components/fields/EnumSelect';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { getSelectedLanguage } from '@frontend/translate';
import { emptyValidationErrors, getValidationErrorTranslation, useAbortableFetch } from '@frontend/utils';
import { assertHasAuthorization } from '@frontend/utils/assertHasAuthorization';
import { type DatePeriod, handleValidatePeriod, validateDatePeriodSchema } from '@frontend/utils/validateDatePeriod';

interface IJob {
    duration: number | null;
    id: string;
    name: string | null;
    output: string | null;
    params: any[] | string | null;
    scheduledOn: string | null;
    startedOn: string | null;
    status: JobStatus;
}

export function JobsList() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const page = tryParseInt(searchParams.getOptional('page'), 1);
    const pageSize = tryParseInt(searchParams.getOptional('pageSize'), 20);
    const [items, setItems] = useState<IJob[]>([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [searchName, setSearchName] = useState<string>('');
    const [searchStatus, setSearchStatus] = useState<JobStatus | null>(null);
    const [datePeriod, setDatePeriod] = useState<DatePeriod | null>(null);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [nameSortDirection, setNameSortDirection] = useState<SortDirection>();
    const abortableFetch = useAbortableFetch();

    const translate = getSelectedLanguage();

    assertHasAuthorization(ManagerAuthorization.jobs);

    useEffect(() => {
        loadData();
    }, [page, pageSize, nameSortDirection]);

    return (
        <PageTemplate>
            <PageHeader title={translate.jobs.jobs} />
            <Card>
                <Card.Body>
                    <Box
                        alignItems="start"
                        display="grid"
                        gap={3}
                        gridTemplateColumns={[
                            '1fr',
                            null,
                            '1fr max-content 1fr',
                            '200px max-content max-content 1fr max-content max-content',
                        ]}
                    >
                        <FormGroup
                            label={translate.jobs.status}
                            layout="vertical"
                        >
                            <EnumSelect
                                enumObj={JobStatus}
                                isClearable={true}
                                labels={translate.jobStatusEnum}
                                onChange={(option) =>
                                    setSearchStatus(option?.value ? (option.value as JobStatus) : null)
                                }
                                placeholder={`${translate.common.select}...`}
                                value={searchStatus}
                            />
                        </FormGroup>
                        <FormGroup
                            hasErrors={errors.has('startDate')}
                            label={translate.jobs.ranAfterDate}
                            layout="vertical"
                        >
                            <DatePicker
                                error={getValidationErrorTranslation(errors.get('startDate'))}
                                key="ranAfterDate"
                                onChange={(value) => {
                                    const updatedDatePeriod = { ...datePeriod, startDate: value };
                                    handleValidatePeriod({
                                        datePeriod: updatedDatePeriod,
                                        errors,
                                        schema: validateDatePeriodSchema,
                                        setErrors,
                                    });
                                    setDatePeriod(updatedDatePeriod);
                                }}
                                value={datePeriod?.startDate ?? null}
                            />
                        </FormGroup>
                        <FormGroup
                            hasErrors={errors.has('endDate')}
                            label={translate.jobs.ranBeforeDate}
                            layout="vertical"
                        >
                            <DatePicker
                                error={getValidationErrorTranslation(errors.get('endDate'))}
                                key="ranBeforeDate"
                                onChange={(value) => {
                                    const updatedDatePeriod = { ...datePeriod, endDate: value };
                                    handleValidatePeriod({
                                        datePeriod: updatedDatePeriod,
                                        errors,
                                        schema: validateDatePeriodSchema,
                                        setErrors,
                                    });
                                    setDatePeriod(updatedDatePeriod);
                                }}
                                value={datePeriod?.endDate ?? null}
                            />
                        </FormGroup>
                        <FormGroup
                            label={translate.jobs.name}
                            layout="vertical"
                        >
                            <TextInput
                                key="jobName"
                                onChange={(e) => setSearchName(e.target.value)}
                                onEnterKeyPress={async () => await applySearchFilter()}
                                placeholder={`${translate.jobs.name}...`}
                                value={searchName}
                            />
                        </FormGroup>
                        <Button
                            onClick={async () => await applySearchFilter()}
                            sx={{ mt: [2, null, 8] }}
                        >
                            {translate.common.search}
                        </Button>
                        <Link
                            color="subtle"
                            fontWeight="normal"
                            onClick={clearFilters}
                            sx={{ mt: [2, null, 42] }}
                            variant="button"
                        >
                            {translate.common.deleteAll}
                        </Link>
                    </Box>
                </Card.Body>
            </Card>
            <Card>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableHeader
                                    isSortable={true}
                                    isSortHeader={!!nameSortDirection}
                                    onClick={handleOrderByName}
                                    sortDirection={nameSortDirection}
                                >
                                    {translate.jobs.name}
                                </TableHeader>
                                <TableHeader>{translate.jobs.status}</TableHeader>
                                <TableHeader>{translate.jobs.scheduledOn}</TableHeader>
                                <TableHeader></TableHeader>
                                <TableHeader>{translate.jobs.params}</TableHeader>
                                <TableHeader>{translate.jobs.startedOn}</TableHeader>
                                <TableHeader>{translate.jobs.duration}</TableHeader>
                                <TableHeader>{translate.jobs.output}</TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((job) => (
                                <TableRow key={job.id}>
                                    <TableCell>{job.name}</TableCell>
                                    <TableCell>{translate.jobStatusEnum[job.status].toLowerCase()}</TableCell>
                                    <TableCell>
                                        <NullableFormattedDate value={job.scheduledOn} />{' '}
                                        <NullableFormattedTime value={job.scheduledOn} />
                                    </TableCell>
                                    <TableCell>
                                        {job.status === JobStatus.scheduled && (
                                            <Button
                                                color="secondary"
                                                onClick={() => {
                                                    runNow(job);
                                                }}
                                                variant="link"
                                            >
                                                {translate.jobs.runNow}
                                            </Button>
                                        )}
                                    </TableCell>
                                    <TableCell>{job.params}</TableCell>
                                    <TableCell>
                                        <NullableFormattedDate value={job.startedOn} />{' '}
                                        <NullableFormattedTime value={job.startedOn} />
                                    </TableCell>
                                    <TableCell>{job.duration}</TableCell>
                                    <TableCell>{job.output}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Card.Footer>
                    <Pagination
                        isDisabled={loading}
                        onChange={(pagingParams) => navigateToPage(pagingParams.page, pagingParams.pageSize)}
                        page={page}
                        pageSize={pageSize}
                        totalItems={total}
                    />
                </Card.Footer>
            </Card>
        </PageTemplate>
    );

    async function runNow(job: IJob) {
        const url = '/api/jobs/' + job.id;
        await abortableFetch.fetchJson(url, 'POST', { scheduledOn: new Date().toISOString() });
        await loadData();
    }

    async function applySearchFilter() {
        if (page === 1) {
            await loadData();
        } else {
            navigateToPage(1, 20);
        }
    }

    async function loadData() {
        let url = `/api/jobs?page=${page}&limit=${pageSize}`;

        if (searchName) {
            url += '&name=' + encodeURIComponent(searchName);
        }
        if (searchStatus) {
            url += '&status=' + searchStatus;
        }
        if (datePeriod?.startDate) {
            url += '&startedAfterDate=' + DateTime.from(datePeriod?.startDate).format('YYYY-MM-DD');
        }
        if (datePeriod?.endDate) {
            url += '&startedBeforeDate=' + DateTime.from(datePeriod?.endDate).format('YYYY-MM-DD');
        }

        if (nameSortDirection) {
            url += '&nameSortDirection=' + nameSortDirection;
        }

        if (errors.size !== 0) {
            return;
        }

        try {
            setLoading(true);
            const result = await abortableFetch.fetchJson(url, 'GET');

            setItems(result.json.items);
            setTotal(result.json.total);
        } finally {
            setLoading(false);
        }
    }

    function navigateToPage(toPage: number, toPageSize: number) {
        if (page !== toPage || pageSize !== toPageSize) {
            navigate(`/jobs?page=${toPage}&pageSize=${toPageSize}`);
        }
    }

    function handleOrderByName() {
        if (!nameSortDirection || nameSortDirection === SortDirection.descending) {
            setNameSortDirection(SortDirection.ascending);
        } else {
            setNameSortDirection(SortDirection.descending);
        }
    }

    async function clearFilters() {
        setSearchName('');
        setSearchStatus(null);
        setDatePeriod(null);
        setErrors(emptyValidationErrors);
        setNameSortDirection(undefined);

        try {
            setLoading(true);
            const result = await abortableFetch.fetchJson(`/api/jobs?page=${page}&limit=${pageSize}`, 'GET');
            setItems(result.json.items);
            setTotal(result.json.total);
        } finally {
            setLoading(false);
        }
    }
}
